import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useNotificationSummary } from '@/lib/assistant';
import { restorePush } from '@/lib/push';
import { NotificationCenter, type CenterTab } from './NotificationCenter';
import './notifications.css';

/** 其他元件（例如小哨的登入問候）要打開通知中心時，丟這個事件。 */
export const OPEN_NOTIFICATIONS = 'edgeai:open-notifications';
export function openNotifications(tab: CenterTab = 'inbox') {
  window.dispatchEvent(new CustomEvent(OPEN_NOTIFICATIONS, { detail: { tab } }));
}

/** 接 Service Worker 的訊息：收到推播就重抓未讀數，點了系統通知就切到對應頁面。
 *  登入後也順便把這個帳號之前開過的推播重新綁回來（登出時伺服器端已撤銷）。 */
function usePushBridge() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const account = user?.username ?? '';

  useEffect(() => { if (account) void restorePush(account); }, [account]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; link?: string } | null;
      if (data?.type === 'edgeai:push') void qc.invalidateQueries({ queryKey: ['notifications'] });
      if (data?.type === 'edgeai:navigate' && data.link?.startsWith('/') && !data.link.startsWith('//')) navigate(data.link);
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [qc, navigate]);
}

export function NotificationBell() {
  usePushBridge();
  const { data } = useNotificationSummary();
  const unread = typeof data?.unread === 'number' ? data.unread : 0;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<CenterTab>('inbox');
  const [ringing, setRinging] = useState(false);
  const previous = useRef<number | null>(null);

  // 未讀數變多時搖一下鈴鐺；第一次載入不搖，免得每次開頁都在晃
  useEffect(() => {
    if (previous.current !== null && unread > previous.current) setRinging(true);
    previous.current = unread;
  }, [unread]);

  useEffect(() => {
    const handler = (event: Event) => {
      const next = (event as CustomEvent<{ tab?: CenterTab }>).detail?.tab;
      setTab(next === 'settings' ? 'settings' : 'inbox');
      setOpen(true);
    };
    window.addEventListener(OPEN_NOTIFICATIONS, handler);
    return () => window.removeEventListener(OPEN_NOTIFICATIONS, handler);
  }, []);

  const label = unread > 0 ? `通知，${unread} 則未讀` : '通知';
  return (
    <>
      <button type="button" aria-label={label} title={label} aria-haspopup="dialog"
        onClick={() => { setTab('inbox'); setOpen(true); }}
        onAnimationEnd={(event) => event.animationName === 'bell-ring' && setRinging(false)}
        className={`relative grid h-11 w-11 shrink-0 place-items-center rounded-sm border border-border
                    text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring${ringing ? ' bell-ring' : ''}`}>
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && <span className="bell-badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
      </button>
      <NotificationCenter open={open} onOpenChange={setOpen} tab={tab} onTabChange={setTab} />
    </>
  );
}
